import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AvailEntryForm, cellLabel } from './availTimeFields';
import './ScheduleSheet.css';

export const SHIFT_PRESETS = [
  { label: '8-17', start: 8, end: 17 },
  { label: '8-12', start: 8, end: 12 },
  { label: '12-17', start: 12, end: 17 },
  { label: '12-21', start: 12, end: 21 },
  { label: '17-21', start: 17, end: 21 },
  { label: '17-24', start: 17, end: 24 },
  { label: '10.5-19.5', start: 10.5, end: 19.5 },
  { label: '11.5-20.5', start: 11.5, end: 20.5 },
  { label: '16.5-20.5', start: 16.5, end: 20.5 },
  { label: '收尾21-01', start: 21, end: 25 },
  { label: '維護11.5-20.5', start: 11.5, end: 20.5 },
  { label: '辦公/櫃台支援 8-12', start: 8, end: 12 },
  { label: '辦公/櫃台支援 17-20', start: 17, end: 20 },
];

export const TAG_LABEL = {
  parttime: '櫃台兼職',
  fulltime: '櫃台正職',
  support: '支援櫃台正職',
  other: '其餘正職',
  venue: '包場',
};

export const TAG_ORDER = ['parttime', 'fulltime', 'support', 'other', 'venue'];

export function shiftStyle(label, start, end) {
  const t = String(label || '');
  if (/收尾/.test(t)) return { background: '#FF9900', color: '#111' };
  if (/維護/.test(t)) return { background: '#76A5AF', color: '#111' };
  if (/支援/.test(t)) return { background: '#46BDC6', color: '#111' };
  if (t === '8-17' || (start === 8 && end === 17)) return { background: '#E06666', color: '#111' };
  if (t === '8-16') return { background: '#EA9999', color: '#111' };
  if (t === '8-12') return { background: '#F4CCCC', color: '#111' };
  if (t === '12-17') return { background: '#B6D7A8', color: '#111' };
  if (t === '17-21') return { background: '#C9DAF8', color: '#111' };
  if (t === '12-21') return { background: '#6D9EEB', color: '#fff' };
  if (t === '10.5-19.5') return { background: '#8E7CC3', color: '#fff' };
  if (Number(start) >= 17 && Number(end) >= 24) return { background: '#0B5394', color: '#fff' };
  if (t) return { background: '#FFD966', color: '#111' };
  return {};
}

function formatAvail(entry) {
  return cellLabel(entry) || '';
}

function hoursOf(a) {
  if (!a) return 0;
  const span = Number(a.end) - Number(a.start);
  if (!Number.isFinite(span) || span <= 0) return 0;
  return span >= 6 ? span - 1 : span;
}

function dayNoteKey(phone, day) {
  return `${phone}|${day}`;
}

function limitFlags(person, a, month, day) {
  const flags = [];
  if (!a || !person) return flags;
  const L = person.limits || {};
  const [y, mo] = String(month).split('-').map(Number);
  const wd = new Date(y, mo - 1, Number(day)).getDay();
  const no = L.no_weekdays || L.noWeekdays || [];
  if (no.includes(wd)) flags.push('禁星期');
  if (L.latest_end_time != null && Number(a.end) > Number(L.latest_end_time)) flags.push('過晚');
  if (person.isLead && (person.tag === 'venue' || person.identityGroup === 'venue')) {
    if (Number(a.start) !== 10 || Number(a.end) !== 19) flags.push('主負責須10-19');
  }
  return flags;
}

/**
 * 班表試算表：凍結左欄與表頭；每人三列（排班／備註／可上）
 */
export default function ScheduleSheet({
  month,
  dayHeaders = [],
  people = [],
  assignments = [],
  activitiesByDay = {},
  shopNotesByDay = {},
  volunteersByDay = {},
  dayNotes = {},
  availabilityByPerson = {},
  canEdit = false,
  canEditAvailability = false,
  showAvailability = true,
  highlightPhone = '',
  compact = false,
  submittedPhones = null,
  reviewDays = [],
  gapList = [],
  onJumpDay,
  saveStatus = '',
  onPatch,
  fillViewport = false,
  minePhone = '',
  view = 'all',
  variant = 'default',
}) {
  const scroller = useRef(null);
  const [todayOverlay, setTodayOverlay] = useState(null);
  const [legendOpen, setLegendOpen] = useState(true);
  const [tagFilter, setTagFilter] = useState([]);
  const [q, setQ] = useState('');
  const [edit, setEdit] = useState(null);
  const [draft, setDraft] = useState('');
  const [dense, setDense] = useState(!!compact);

  const assignMap = useMemo(() => {
    const m = {};
    for (const a of assignments) {
      m[`${a.person}|${a.day}`] = a;
      if (a.staffPhone) m[`${a.staffPhone}|${a.day}`] = a;
    }
    return m;
  }, [assignments]);

  const visiblePeople = useMemo(() => {
    let list = people;
    if (view === 'mine' && minePhone) list = list.filter((p) => p.staffPhone === minePhone);
    if (tagFilter.length) list = list.filter((p) => tagFilter.includes(p.tag || p.identityGroup));
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      list = list.filter((p) => String(p.person || p.displayName || '').toLowerCase().includes(s));
    }
    return list;
  }, [people, view, minePhone, tagFilter, q]);

  const stats = useMemo(() => {
    const out = {};
    for (const p of people) {
      let days = 0;
      let hrs = 0;
      for (const h of dayHeaders) {
        const a = assignMap[`${p.staffPhone}|${h.day}`] || assignMap[`${p.person}|${h.day}`];
        if (a) { days += 1; hrs += hoursOf(a); }
      }
      out[p.staffPhone] = { days, hrs };
    }
    return out;
  }, [people, dayHeaders, assignMap]);

  const coverageGaps = useMemo(() => {
    const set = new Set();
    for (const d of reviewDays || []) set.add(Number(d.day || d));
    return set;
  }, [reviewDays]);

  const updateTodayOverlay = useCallback(() => {
    const root = scroller.current;
    if (!root) { setTodayOverlay(null); return; }
    const table = root.querySelector('.ss-table');
    const todayHeader = root.querySelector('th[data-ss-day].ss-col-today');
    if (!table || !todayHeader) { setTodayOverlay(null); return; }
    const scrollRect = root.getBoundingClientRect();
    const tableRect = table.getBoundingClientRect();
    const colRect = todayHeader.getBoundingClientRect();
    setTodayOverlay({
      left: colRect.left - scrollRect.left + root.scrollLeft,
      top: tableRect.top - scrollRect.top + root.scrollTop,
      width: colRect.width,
      height: table.offsetHeight,
    });
  }, []);

  useEffect(() => {
    if (!scroller.current) return;
    const todayEl = scroller.current.querySelector('.ss-col-today');
    if (todayEl) todayEl.scrollIntoView({ inline: 'center', block: 'nearest' });
    const t = window.setTimeout(updateTodayOverlay, 0);
    return () => window.clearTimeout(t);
  }, [month, dayHeaders.length, visiblePeople.length, showAvailability, updateTodayOverlay]);

  useEffect(() => {
    const root = scroller.current;
    if (!root) return undefined;
    root.addEventListener('scroll', updateTodayOverlay);
    window.addEventListener('resize', updateTodayOverlay);
    return () => {
      root.removeEventListener('scroll', updateTodayOverlay);
      window.removeEventListener('resize', updateTodayOverlay);
    };
  }, [updateTodayOverlay]);

  const jumpToday = () => {
    const el = scroller.current?.querySelector('.ss-col-today');
    el?.scrollIntoView({ inline: 'center', block: 'nearest' });
  };

  const jumpGap = () => {
    const el = scroller.current?.querySelector('.ss-gap-dot');
    el?.scrollIntoView({ inline: 'center', block: 'nearest' });
  };

  const openEdit = (kind, person, day, initial) => {
    if (!canEdit && !(kind === 'availability' && canEditAvailability)) return;
    if (kind === 'availability' && !canEditAvailability) return;
    setEdit({ kind, person, day, staffPhone: person.staffPhone });
    setDraft(initial || '');
  };

  const commit = async (extra = {}) => {
    if (!edit || !onPatch) { setEdit(null); return; }
    const payload = { kind: edit.kind, day: edit.day, person: edit.person.person, staffPhone: edit.staffPhone, ...extra };
    if (edit.kind === 'note' || edit.kind === 'shop' || edit.kind === 'volunteer' || edit.kind === 'personNote') {
      payload.text = extra.text !== undefined ? extra.text : draft;
    }
    await onPatch(payload);
    setEdit(null);
  };

  const toggleTag = (t) => {
    setTagFilter((cur) => (cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]));
  };

  let lastTag = null;

  const isAdmin = variant === 'admin';

  return (
    <div className={`ss-wrap ${dense ? 'ss-dense' : ''}${fillViewport ? ' ss-fill-viewport' : ''}${isAdmin ? ' ss-admin' : ''}`}>
      {!isAdmin && (
      <div className="ss-toolbar">
        <div className="ss-toolbar-row">
          {TAG_ORDER.map((t) => (
            <button
              key={t}
              type="button"
              className={`ss-chip ${tagFilter.includes(t) ? 'on' : ''}`}
              onClick={() => toggleTag(t)}
            >{TAG_LABEL[t]}</button>
          ))}
          <input className="ss-search" placeholder="搜尋姓名" value={q} onChange={(e) => setQ(e.target.value)} />
          <button type="button" className="ss-chip" onClick={jumpToday}>今日</button>
          <button type="button" className="ss-chip" onClick={jumpGap}>缺口</button>
          <button type="button" className="ss-chip" onClick={() => setDense((v) => !v)}>{dense ? '舒適' : '緊湊'}</button>
          <span className="ss-hours-hint">滿6h扣1h休息</span>
          <button type="button" className="ss-chip" onClick={() => setLegendOpen((v) => !v)}>{legendOpen ? '收合色票' : '色票'}</button>
          {saveStatus ? <span className="ss-save">{saveStatus}</span> : null}
        </div>
        {legendOpen && (
          <div className="ss-legend">
            {SHIFT_PRESETS.slice(0, 11).map((p) => (
              <span key={p.label} className="ss-leg-item" style={shiftStyle(p.label, p.start, p.end)}>{p.label}</span>
            ))}
            <span className="ss-leg-item" style={shiftStyle('客製', 0, 0)}>客製</span>
          </div>
        )}
      </div>
      )}
      {isAdmin && saveStatus ? (
        <div className="ss-admin-bar"><span className="ss-save">{saveStatus}</span></div>
      ) : null}

      {gapList?.length > 0 && (
        <div className="ss-gap-panel">
          <h4>需人工確認（{gapList.length}）</h4>
          {gapList.slice(0, 12).map((g, i) => (
            <button
              key={i}
              type="button"
              className="ss-gap-item"
              onClick={() => onJumpDay ? onJumpDay(g.day) : jumpGap()}
            >{g.day} 日 · {g.slot ? `${g.slot} · ` : ''}{g.reason}</button>
          ))}
          {gapList.length > 12 ? <div style={{ fontSize: 11, color: '#9ca3af' }}>…還有 {gapList.length - 12} 項</div> : null}
        </div>
      )}

      <div className="ss-scroll" ref={scroller}>
        {todayOverlay ? (
          <div
            className="ss-today-col-overlay"
            style={{
              left: todayOverlay.left,
              top: todayOverlay.top,
              width: todayOverlay.width,
              height: todayOverlay.height,
            }}
          />
        ) : null}
        <table className="ss-table">
          <thead>
            <tr>
              <th className="ss-sticky ss-left">活動</th>
              {dayHeaders.map((h) => (
                <th key={`a${h.day}`} className={`ss-act-cell ${h.weekend ? 'ss-wk' : ''} ${h.today ? 'ss-col-today' : ''}`}>
                  <span className="ss-act">{activitiesByDay[h.day] || ''}</span>
                </th>
              ))}
              <th className="ss-note-col" />
            </tr>
            <tr>
              <th className="ss-sticky ss-left">店務</th>
              {dayHeaders.map((h) => (
                <th
                  key={`s${h.day}`}
                  className={`${h.weekend ? 'ss-wk' : ''} ${h.today ? 'ss-col-today' : ''} ${canEdit ? 'ss-click' : ''}`}
                  onClick={() => canEdit && openEdit('shop', { person: '', staffPhone: '' }, h.day, shopNotesByDay[h.day] || shopNotesByDay[String(h.day)] || '')}
                >
                  <span className="ss-act-shop">{shopNotesByDay[h.day] || shopNotesByDay[String(h.day)] || ''}</span>
                </th>
              ))}
              <th className="ss-note-col" />
            </tr>
            <tr>
              <th className="ss-sticky ss-left">日期</th>
              {dayHeaders.map((h) => (
                <th key={`d${h.day}`} data-ss-day={h.day} className={`${h.weekend ? 'ss-wk' : ''} ${h.redDay ? 'ss-red-day' : ''} ${h.today ? 'ss-col-today' : ''}`}>
                  {h.day}
                  {coverageGaps.has(h.day) ? <span className="ss-gap-dot" title="需人工確認" /> : null}
                </th>
              ))}
              <th className="ss-note-col">備註</th>
            </tr>
            <tr>
              <th className="ss-sticky ss-left">星期</th>
              {dayHeaders.map((h) => (
                <th key={`w${h.day}`} className={`${h.weekend ? 'ss-wk' : ''} ${h.today ? 'ss-col-today' : ''}`}>{h.wd}</th>
              ))}
              <th className="ss-note-col" />
            </tr>
            <tr>
              <th className="ss-sticky ss-left">志工</th>
              {dayHeaders.map((h) => (
                <th
                  key={`v${h.day}`}
                  className={`${h.weekend ? 'ss-wk' : ''} ${h.today ? 'ss-col-today' : ''} ${canEdit ? 'ss-click' : ''}`}
                  onClick={() => canEdit && openEdit('volunteer', { person: '', staffPhone: '' }, h.day, volunteersByDay[h.day] || volunteersByDay[String(h.day)] || '')}
                >
                  {volunteersByDay[h.day] || volunteersByDay[String(h.day)] || ''}
                </th>
              ))}
              <th className="ss-note-col" />
            </tr>
          </thead>
          <tbody>
            {visiblePeople.map((p) => {
              const tag = p.tag || p.identityGroup;
              const showSep = lastTag && lastTag !== tag;
              lastTag = tag;
              const submitted = !submittedPhones || submittedPhones.has(p.staffPhone);
              const st = stats[p.staffPhone] || { days: 0, hrs: 0 };
              const cap = p.limits?.monthly_hour_cap;
              const over = cap != null && st.hrs > cap;
              const me = highlightPhone && p.staffPhone === highlightPhone;
              return (
                <React.Fragment key={p.staffPhone}>
                  {showSep && (
                    <tr className="ss-sep">
                      <td className="ss-sticky ss-left">{TAG_LABEL[tag] || tag}</td>
                      {dayHeaders.map((h) => <td key={h.day} />)}
                      <td />
                    </tr>
                  )}
                  <tr className={me ? 'ss-me' : ''}>
                    <td className={`ss-sticky ss-left ss-name ${submitted ? '' : 'ss-unsent'}`}>
                      {p.person}{p.isLead ? ' ★' : ''}
                      <div className="ss-hrs" style={{ color: over ? '#b91c1c' : undefined }}>{st.days}天 · {st.hrs.toFixed(1)}h</div>
                    </td>
                    {dayHeaders.map((h) => {
                      const a = assignMap[`${p.staffPhone}|${h.day}`] || assignMap[`${p.person}|${h.day}`];
                      const flags = limitFlags(p, a, month, h.day);
                      return (
                        <td
                          key={h.day}
                          className={`${h.today ? 'ss-col-today' : ''} ${canEdit ? 'ss-click' : ''}`}
                          style={a ? shiftStyle(a.label, a.start, a.end) : { color: '#d1d5db' }}
                          onClick={() => canEdit && setEdit({ kind: 'shift', person: p, day: h.day, staffPhone: p.staffPhone, current: a })}
                        >
                          {a?.label || '—'}
                          {flags.length ? <span className="ss-flag" title={flags.join('、')}>!</span> : null}
                        </td>
                      );
                    })}
                    <td
                      className={`ss-note-col ${canEdit ? 'ss-click' : ''}`}
                      onClick={() => canEdit && openEdit('personNote', p, 0, p.personNote || '')}
                    >{p.personNote || ''}</td>
                  </tr>
                  <tr className="ss-note-row">
                    <td className="ss-sticky ss-left ss-sub">備註</td>
                    {dayHeaders.map((h) => {
                      const text = dayNotes[dayNoteKey(p.staffPhone, h.day)] || '';
                      return (
                        <td
                          key={h.day}
                          className={`${h.today ? 'ss-col-today' : ''} ${canEdit ? 'ss-click' : ''}`}
                          onClick={() => canEdit && openEdit('note', p, h.day, text)}
                        >{text}</td>
                      );
                    })}
                    <td />
                  </tr>
                  {showAvailability && (
                    <tr className="ss-avail-row">
                      <td className="ss-sticky ss-left ss-sub">可上</td>
                      {dayHeaders.map((h) => {
                        const e = availabilityByPerson[p.person]?.[h.day] || availabilityByPerson[p.staffPhone]?.[h.day];
                        const text = formatAvail(e);
                        return (
                          <td
                            key={h.day}
                            className={`${h.today ? 'ss-col-today' : ''} ${e?.base === '休' && !(e?.overrides || []).length ? 'ss-off' : ''} ${canEditAvailability ? 'ss-click' : ''}`}
                            onClick={() => canEditAvailability && setEdit({
                              kind: 'availability', person: p, day: h.day, staffPhone: p.staffPhone, current: e,
                            })}
                          >{text || ''}</td>
                        );
                      })}
                      <td />
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {edit && (
        <div className="ss-modal-overlay" onClick={() => setEdit(null)}>
          <div className="ss-modal" onClick={(e) => e.stopPropagation()}>
            {edit.kind === 'shift' && (
              <>
                <div className="ss-modal-title">改班 · {edit.person.person} · {edit.day}日</div>
                <div className="ss-pop-btns">
                  {SHIFT_PRESETS.map((p) => {
                    const hrs = hoursOf(p);
                    return (
                      <button key={p.label} type="button" onClick={() => commit({ label: p.label, start: p.start, end: p.end })}>
                        {p.label}{hrs ? ` (${hrs}h)` : ''}
                      </button>
                    );
                  })}
                  <button type="button" onClick={() => commit({ clear: true })}>清除</button>
                </div>
                <div className="ss-custom">
                  <input placeholder="自訂標籤" value={draft} onChange={(e) => setDraft(e.target.value)} />
                  <input type="number" step="0.5" placeholder="開始" id="ss-cs" />
                  <input type="number" step="0.5" placeholder="結束" id="ss-ce" />
                  <button type="button" onClick={() => {
                    const start = Number(document.getElementById('ss-cs')?.value);
                    const end = Number(document.getElementById('ss-ce')?.value);
                    if (!draft || !Number.isFinite(start) || !Number.isFinite(end)) return;
                    commit({ label: draft, start, end });
                  }}>套用自訂</button>
                </div>
                <div className="ss-modal-btns">
                  <button type="button" onClick={() => setEdit(null)}>取消</button>
                </div>
              </>
            )}
            {(edit.kind === 'note' || edit.kind === 'shop' || edit.kind === 'volunteer' || edit.kind === 'personNote') && (
              <>
                <div className="ss-modal-title">
                  {edit.kind === 'personNote' ? '個人規則描述' : edit.kind === 'shop' ? `店務 · ${edit.day}日` : edit.kind === 'volunteer' ? `志工 · ${edit.day}日` : `當日備註 · ${edit.person.person} · ${edit.day}日`}
                </div>
                {edit.kind === 'personNote' ? (
                  <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={5} />
                ) : (
                  <input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} style={{ width: '100%', padding: 8 }} />
                )}
                <div className="ss-modal-btns">
                  <button type="button" onClick={() => commit()}>儲存</button>
                  <button type="button" onClick={() => commit({ text: '' })}>清除</button>
                  <button type="button" onClick={() => setEdit(null)}>取消</button>
                </div>
              </>
            )}
            {edit.kind === 'availability' && (
              <>
                <div className="ss-modal-title">可上 · {edit.person.person} · {edit.day}日</div>
                <AvailEntryForm
                  day={edit.day}
                  initial={edit.current}
                  onCommit={(entry) => commit({ entry })}
                  onCancel={() => setEdit(null)}
                  allowClear
                />
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
